/* Quick Order application logic.
   Depends on window.BCSProductLookup from assets/bcs-product-lookup.js. Keep shared commerce helpers out of this file. */

(function () {
  'use strict';

  // ---------- Module reference (assigned in qoInit, after deferred module loads) ----------
  // The section loads bcs-product-lookup.js before this asset with defer. We still capture
  // the reference at init time so section render order and future theme-editor reloads do not race.
  var BCS = null;
  var QO_CONFIG = { customerTags: [], algoliaIndex: 'bcs_production_products' };
  var QO_CUSTOMER_TAGS = [];
  var QO_ALGOLIA_INDEX = 'bcs_production_products';


  function readQuickOrderConfig() {
    var config = { customerTags: [], algoliaIndex: 'bcs_production_products' };
    var el = document.getElementById('qo-config');
    if (!el) return config;
    try {
      var parsed = JSON.parse(el.textContent || '{}');
      if (parsed && typeof parsed === 'object') {
        if (Array.isArray(parsed.customerTags)) {
          config.customerTags = parsed.customerTags;
        } else if (typeof parsed.customerTags === 'string') {
          config.customerTags = parsed.customerTags.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        }
        if (typeof parsed.algoliaIndex === 'string' && parsed.algoliaIndex.trim()) {
          config.algoliaIndex = parsed.algoliaIndex.trim();
        }
      }
    } catch (e) {
      console.error('[BCS Quick Order] config parse failed', e);
    }
    return config;
  }

  function qoConfigureLookup() {
    if (!BCS || typeof BCS.configure !== 'function') return;
    BCS.configure({ algoliaIndex: QO_ALGOLIA_INDEX, customerTags: QO_CUSTOMER_TAGS });
  }

  // ---------- State ----------

  var QO_INITIAL_ROWS = 3;
  var QO_STORAGE_KEY = 'bcs:qo:state:v1';
  var qoInitialized = false;
  var qoInitAttempts = 0;
  var qoNextId = 1;
  var qoRows = [];
  var qoAffordanceBound = false;
  var qoSearchTimers = {};
  var qoSaveTimer = null;
  var qoActiveDropdownRowId = null;
  var qoModalReturnFocus = null;

  // ---------- Persistence (localStorage) ----------
  // Persists the row state across refreshes / page navigation. Cleared automatically when the
  // user empties the row list or completes an Add To Cart (which resets to QO_INITIAL_ROWS).
  // Fails soft if storage is unavailable (private browsing, quota, etc.).

  function qoSerialize() {
    var rows = qoRows.map(function (r) {
      // Strip ephemeral runtime-only fields (_picking, _dropdownHits, etc.) — only the
      // declarative row state goes to storage.
      return {
        id: r.id, state: r.state, query: r.query, qty: r.qty,
        handle: r.handle, variantId: r.variantId, title: r.title,
        sku: r.sku, vendor: r.vendor, price: r.price, available: r.available,
        priceStatus: r.priceStatus,
        inventoryQuantity: r.inventoryQuantity, inventoryTracked: r.inventoryTracked,
        restrictedClassId: r.restrictedClassId, restricted: r.restricted,
        discontinued: r.discontinued,
        directAlternatives: Array.isArray(r.directAlternatives) ? r.directAlternatives : [],
        functionalAlternatives: Array.isArray(r.functionalAlternatives) ? r.functionalAlternatives : [],
        status: r.status,
        unmatched: !!r.unmatched,
        didYouMeanLabel: r.didYouMeanLabel || '',
        didYouMeanHit: r.didYouMeanHit || null
      };
    });
    return { v: 1, nextId: qoNextId, rows: rows };
  }

  function qoSaveStateNow() {
    if (qoSaveTimer) {
      clearTimeout(qoSaveTimer);
      qoSaveTimer = null;
    }
    try {
      localStorage.setItem(QO_STORAGE_KEY, JSON.stringify(qoSerialize()));
    } catch (e) { /* fail-soft */ }
  }

  function qoSaveState() {
    if (qoSaveTimer) clearTimeout(qoSaveTimer);
    qoSaveTimer = setTimeout(qoSaveStateNow, 300);
  }

  window.addEventListener('beforeunload', qoSaveStateNow);

  function qoClearStoredState() {
    try { localStorage.removeItem(QO_STORAGE_KEY); } catch (e) { /* fail-soft */ }
  }

  function qoLoadState() {
    try {
      var raw = localStorage.getItem(QO_STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || parsed.v !== 1 || !Array.isArray(parsed.rows)) {
        qoClearStoredState();
        return null;
      }
      return parsed;
    } catch (e) {
      qoClearStoredState();
      return null;
    }
  }

  function qoNormalizeRowStatus(r) {
    if (!r) return;
    if (r.restricted) {
      r.status = 'restricted';
    } else if (r.discontinued) {
      r.directAlternatives = Array.isArray(r.directAlternatives) ? r.directAlternatives : [];
      r.functionalAlternatives = Array.isArray(r.functionalAlternatives) ? r.functionalAlternatives : [];
      r.status = (r.directAlternatives.length || r.functionalAlternatives.length) ? 'replacement' : 'discontinued';
    } else {
      r.status = 'exact';
    }
  }

  // Stale-while-revalidate: after restoring rows from storage, re-fetch bom-meta for each
  // unique resolved-row handle to refresh B2B price / inventory / discontinued / restricted.
  // This catches changes since the last save (price-list adjustments, inventory changes,
  // customer-tag changes, etc.). Failures fall back silently to the stored values.
  function qoRevalidateResolvedRows() {
    var seen = {};
    var markedPending = false;
    qoRows.forEach(function (r) {
      if (r.state !== 'resolved' || !r.handle) return;
      r.priceStatus = 'pending';
      markedPending = true;
    });
    if (markedPending) qoRenderAll();
    qoRows.forEach(function (r) {
      if (r.state !== 'resolved' || !r.handle || seen[r.handle]) return;
      seen[r.handle] = true;
      BCS.fetchBomMeta(r.handle).then(function (meta) {
        var rowsForHandle = qoRows.filter(function (x) { return x.handle === r.handle; });
        if (!meta) {
          rowsForHandle.forEach(function (rh) { rh.priceStatus = 'error'; });
          qoRenderAll();
          return;
        }
        rowsForHandle.forEach(function (rh) {
          var mv = (meta.variants || []).find(function (v) { return String(v.id) === String(rh.variantId); });
          if (mv) {
            var hydratedPrice = (mv.price != null) ? parseFloat(mv.price) : NaN;
            if (isFinite(hydratedPrice)) {
              rh.price = hydratedPrice;
              rh.priceStatus = 'loaded';
            } else {
              rh.priceStatus = 'error';
            }
            rh.available = (mv.available != null) ? mv.available : rh.available;
            rh.inventoryQuantity = mv.inventory_quantity;
            rh.inventoryTracked = !!mv.inventory_management;
          } else {
            rh.priceStatus = 'error';
          }
          if (meta.vendor) rh.vendor = meta.vendor;
          rh.discontinued = !!meta.discontinued;
          rh.restrictedClassId = meta.restricted_class_id || rh.restrictedClassId;
          rh.restricted = BCS.isRestricted(rh.restrictedClassId);
          qoNormalizeRowStatus(rh);
        });
        qoRenderAll();
      }).catch(function () {
        qoRows.forEach(function (rh) {
          if (rh.handle === r.handle && rh.state === 'resolved') rh.priceStatus = 'error';
        });
        qoRenderAll();
      });
    });
  }

  function qoMakeRow(state) {
    return Object.assign({
      id: qoNextId++,
      state: 'empty',
      query: '',
      qty: 1,
      handle: '',
      variantId: null,
      title: '',
      sku: '',
      vendor: '',
      price: 0,
      priceStatus: 'pending',
      available: true,
      inventoryQuantity: null,
      inventoryTracked: false,
      restrictedClassId: '',
      restricted: false,
      discontinued: false,
      directAlternatives: [],
      functionalAlternatives: [],
      status: 'exact',
      // Set to true for paste-derived rows where Algolia returned no hits — renders the
      // "SKU not found" hint in the empty-state input. Cleared implicitly when the row
      // transitions to 'resolved' (the resolved render path doesn't consult this flag).
      unmatched: false,
      didYouMeanLabel: '',
      didYouMeanHit: null,
      _editBackup: null,
      _picking: false
    }, state || {});
  }

  function qoEnsureEntryRows() {
    if (!qoRows.length) {
      for (var i = 0; i < QO_INITIAL_ROWS; i++) qoRows.push(qoMakeRow());
      return;
    }
    var hasEntryRow = qoRows.some(function (r) { return r.state !== 'resolved'; });
    if (!hasEntryRow) qoRows.push(qoMakeRow());
  }

  function qoIsAddable(r) {
    if (r.state !== 'resolved') return false;
    if (r.restricted) return false;
    if (!r.variantId) return false;
    if (!(r.qty >= 1)) return false;
    if (r.priceStatus !== 'loaded') return false;
    // Discontinued products (with or without curated alternatives): only addable if real
    // on-hand inventory is available. The qty is capped at inventoryQuantity in qoSetQty.
    if (r.discontinued) {
      if (typeof r.inventoryQuantity !== 'number' || r.inventoryQuantity <= 0) return false;
      if (r.qty > r.inventoryQuantity) return false;
    }
    return true;
  }

  // True when the row is a discontinued product with no on-hand inventory — non-addable,
  // qty input disabled, price hidden (per business rule).
  function qoIsDiscontinuedNoStock(r) {
    return r.discontinued && (typeof r.inventoryQuantity !== 'number' || r.inventoryQuantity <= 0);
  }

  function qoFindRow(id) {
    for (var i = 0; i < qoRows.length; i++) if (qoRows[i].id === id) return qoRows[i];
    return null;
  }

  // ---------- DOM helpers ----------

  function qoSetStatus(text, kind) {
    var el = document.getElementById('qo-status');
    if (!el) return;
    el.textContent = text || '';
    el.className = 'qo-status' + (kind ? ' ' + kind : '');
    if (text && kind === 'success') {
      setTimeout(function () {
        if (el.textContent === text) qoSetStatus('', null);
      }, 3000);
    }
  }

  function qoUpdateAddToCartButton() {
    var btn = document.getElementById('qo-btn-cart');
    if (!btn) return;
    var anyAddable = qoRows.some(qoIsAddable);
    btn.disabled = qoCartInFlight || !anyAddable;
  }

  function qoUpdateClearAllButton() {
    var btn = document.getElementById('qo-clear-all');
    if (!btn) return;
    btn.disabled = !qoHasClearableContent();
  }

  function qoUpdateSubtotal() {
    var total = 0;
    qoRows.forEach(function (r) {
      if (qoIsAddable(r)) total += (r.price || 0) * (r.qty || 0);
    });
    var el = document.getElementById('qo-subtotal');
    if (el) el.textContent = '$' + total.toFixed(2);
  }

  function qoCssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value || ''));
    }
    return String(value || '').replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  // ---------- Init ----------

  function qoInit() {
    if (qoInitialized) return;
    if (!window.BCSProductLookup) {
      if (qoInitAttempts < 40) {
        qoInitAttempts += 1;
        setTimeout(qoInit, 250);
        return;
      }
      console.error('[BCS Quick Order] BCSProductLookup module not loaded — check section script include');
      return;
    }
    BCS = window.BCSProductLookup;
    QO_CONFIG = readQuickOrderConfig();
    QO_CUSTOMER_TAGS = QO_CONFIG.customerTags;
    QO_ALGOLIA_INDEX = QO_CONFIG.algoliaIndex;
    qoConfigureLookup();
    qoInitialized = true;

    // Restore rows from localStorage if a previous session left state behind. Otherwise
    // start with QO_INITIAL_ROWS empty rows.
    var saved = qoLoadState();
    if (saved && saved.rows && saved.rows.length > 0) {
      qoRows = saved.rows.map(function (s) {
        return Object.assign({ _picking: false }, s);
      });
      // Make sure new rows allocated this session don't collide with restored IDs.
      qoNextId = saved.nextId || 1;
      qoRows.forEach(function (r) { if (r.id >= qoNextId) qoNextId = r.id + 1; });
      qoRows.forEach(function (r) {
        if (r.state !== 'resolved') return;
        r.restricted = BCS.isRestricted(r.restrictedClassId);
        qoNormalizeRowStatus(r);
      });
      qoRenderAll();
      // Refresh B2B price / inventory / restricted / discontinued in the background.
      qoRevalidateResolvedRows();
    } else {
      qoRows = [];
      for (var i = 0; i < QO_INITIAL_ROWS; i++) qoRows.push(qoMakeRow());
      qoRenderAll();
    }

    var addLineBtn = document.getElementById('qo-add-line');
    if (addLineBtn) {
      addLineBtn.onclick = function () {
        qoRows.push(qoMakeRow());
        qoRenderAll();
        setTimeout(function () {
          var newest = qoRows[qoRows.length - 1];
          var input = document.getElementById('qo-sku-' + newest.id);
          if (input) input.focus();
        }, 0);
      };
    }

    var cartBtn = document.getElementById('qo-btn-cart');
    if (cartBtn) cartBtn.onclick = qoAddToCart;

    var clearAllBtn = document.getElementById('qo-clear-all');
    if (clearAllBtn) clearAllBtn.onclick = qoClearAll;

    var clearAllTopBtn = document.getElementById('qo-clear-all-top');
    if (clearAllTopBtn) clearAllTopBtn.onclick = qoClearAll;

    // Re-evaluate the top Clear All on scroll/resize (row state unchanged, viewport changed).
    // Bound once for the page lifetime — qoInit can re-run on shopify:section:load.
    if (!qoAffordanceBound) {
      qoAffordanceBound = true;
      window.addEventListener('resize', qoScheduleTopClearAllUpdate);
      window.addEventListener('scroll', qoScheduleTopClearAllUpdate, { passive: true });
    }

    var pasteBtn = document.getElementById('qo-paste-apply');
    if (pasteBtn) pasteBtn.onclick = qoApplyPaste;

    var modal = document.getElementById('qo-modal');
    if (modal) {
      modal.querySelectorAll('[data-action="close-modal"]').forEach(function (el) {
        el.onclick = qoCloseModalImpl;
      });
      modal.onkeydown = qoHandleModalKeydown;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', qoInit);
  } else {
    qoInit();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target || !event.target.querySelector('#qo-rows')) return;
    qoInitialized = false;
    qoInitAttempts = 0;
    qoInit();
  });

  // ---------- Rendering ----------

  function qoRenderAll() {
    var bodyEl = document.getElementById('qo-rows');
    if (!bodyEl) return;
    var focusHint = qoCaptureFocusHint();
    bodyEl.innerHTML = qoRows.map(qoRenderRow).join('');
    qoRows.forEach(qoBindRowListeners);
    qoUpdateSubtotal();
    qoUpdateAddToCartButton();
    qoUpdateClearAllButton();
    qoUpdateTopClearAll();
    // Persist to localStorage on every render — every state change funnels through here.
    qoSaveState();
    qoRestoreFocusHint(focusHint);
  }

  function qoCaptureFocusHint() {
    var active = document.activeElement;
    if (!active || !active.id || active.id.indexOf('qo-sku-') !== 0) return null;
    var id = parseInt(active.id.replace('qo-sku-', ''), 10);
    if (!id) return null;
    return {
      id: id,
      selectionStart: active.selectionStart,
      selectionEnd: active.selectionEnd,
      dropdownOpen: qoActiveDropdownRowId === id
    };
  }

  function qoRestoreFocusHint(hint) {
    if (!hint) return;
    setTimeout(function () {
      var input = document.getElementById('qo-sku-' + hint.id);
      if (!input) return;
      input.focus();
      if (typeof hint.selectionStart === 'number' && typeof input.setSelectionRange === 'function') {
        var end = typeof hint.selectionEnd === 'number' ? hint.selectionEnd : hint.selectionStart;
        input.setSelectionRange(hint.selectionStart, end);
      }
      if (hint.dropdownOpen && input.value && input.value.length >= QO_MIN_CHARS) {
        qoRunDropdownSearch(hint.id, input.value);
      }
    }, 0);
  }

  function qoBindImageFallbacks(container) {
    if (!container) return;
    container.querySelectorAll('img[data-fallback-class]').forEach(function (img) {
      img.addEventListener('error', function () {
        var fallback = document.createElement('div');
        fallback.className = img.getAttribute('data-fallback-class') || '';
        img.replaceWith(fallback);
      }, { once: true });
    });
  }

  function qoRenderRow(r) {
    if (r.state === 'resolved') return qoRenderResolvedRow(r);
    return qoRenderInputRow(r);
  }

  function qoRenderInputRow(r) {
    var inputClasses = 'qo-input-sku' + (r.unmatched ? ' qo-input-sku-unmatched' : '');
    var inputAria = r.unmatched ? ' aria-invalid="true"' : '';
    var didYouMeanHtml = (r.unmatched && r.didYouMeanLabel)
      ? '<button type="button" class="qo-did-you-mean" data-action="did-you-mean" data-row-id="' + r.id + '">Did you mean ' + BCS.escape(r.didYouMeanLabel) + '?</button>'
      : '';
    var hintHtml = r.unmatched
      ? '<div class="qo-unmatched-hint">SKU not found</div>' + didYouMeanHtml
      : '';
    return ''
      + '<div class="qo-row" data-row-id="' + r.id + '">'
      +   '<div class="qo-row-product-cell">'
      +     '<div class="qo-row-input-wrap">'
      +       '<input class="' + inputClasses + '" id="qo-sku-' + r.id + '" type="text" '
      +         'placeholder="Item SKU" autocomplete="off" aria-autocomplete="list"'
      +         ' aria-controls="qo-dd-' + r.id + '"' + inputAria
      +         ' value="' + BCS.escape(r.query) + '">'
      +       '<div class="qo-dropdown" id="qo-dd-' + r.id + '" role="listbox" style="display:none"></div>'
      +     '</div>'
      +     hintHtml
      +   '</div>'
      +   '<div class="qo-col-qty">' + qoRenderQtyControl(r, !!r._editBackup) + '</div>'
      +   '<div class="qo-col-price"><span class="qo-price qo-price-dash">$—</span></div>'
      +   '<div class="qo-col-remove">'
      +     '<button type="button" class="qo-remove-btn" data-action="remove" data-row-id="' + r.id + '" aria-label="Remove row">×</button>'
      +   '</div>'
      + '</div>';
  }

  // Render the segmented [- value +] qty selector (matches theme's .bcs-qty styling).
  // The middle value is an editable numeric input: typed entry routes through qoSetQty on blur,
  // while +/- still adjust it. disabled=true grays the whole control and disables both buttons
  // and the input (used for discontinued+no-stock and restricted).
  function qoRenderQtyControl(r, disabled) {
    var disAttr = disabled ? ' disabled' : '';
    var disClass = disabled ? ' disabled' : '';
    return ''
      + '<div class="qo-qty-control' + disClass + '" data-row-id="' + r.id + '">'
      +   '<button type="button" class="qo-qty-btn" data-qty-delta="-1" data-row-id="' + r.id + '" aria-label="Decrease quantity"' + disAttr + '>−</button>'
      +   '<input type="text" inputmode="numeric" class="qo-qty-value" id="qo-qty-' + r.id + '" value="' + r.qty + '" data-row-id="' + r.id + '" aria-label="Quantity"' + disAttr + '>'
      +   '<button type="button" class="qo-qty-btn" data-qty-delta="1" data-row-id="' + r.id + '" aria-label="Increase quantity"' + disAttr + '>+</button>'
      + '</div>';
  }

  function qoRenderResolvedRow(r) {
    r.directAlternatives = Array.isArray(r.directAlternatives) ? r.directAlternatives : [];
    r.functionalAlternatives = Array.isArray(r.functionalAlternatives) ? r.functionalAlternatives : [];
    var badge = '';
    var discNote = '';
    var replacementsLink = '';

    if (r.restricted) {
      badge = ' <span class="qo-badge restricted">Restricted</span>';
      discNote = QO_CUSTOMER_TAGS.length
        ? '<div class="qo-disc-note">Restricted product — your account doesn\'t have access.</div>'
        : '<div class="qo-disc-note">Restricted product — sign in to an account with access.</div>';
    } else if (r.status === 'replacement') {
      badge = ' <span class="qo-badge replacement">Replacement available</span>';
      discNote = '<div class="qo-disc-note">Product discontinued - alternative available</div>';
      replacementsLink = '<button type="button" class="qo-replacements-link" data-action="replacements" data-row-id="' + r.id + '">View replacements</button>';
    } else if (r.status === 'discontinued') {
      badge = ' <span class="qo-badge discontinued">Discontinued</span>';
      discNote = '<div class="qo-disc-note">Product discontinued</div>';
    }
    if (!r.restricted && r.priceStatus === 'error') {
      discNote += '<div class="qo-disc-note">Price unavailable — refresh or try again before adding this item.</div>';
    }

    var addable = qoIsAddable(r);
    var noStockDiscontinued = qoIsDiscontinuedNoStock(r);

    // For a discontinued product with no on-hand stock, BCS.stockLine would render
    // "Available to order" (importer's default), which is misleading — discontinued items
    // cannot be reordered. Show a clearer message instead. Restricted products: hide stock
    // entirely — buyer cannot purchase without access, so exposing inventory is misleading.
    var stockHtml;
    if (noStockDiscontinued) {
      stockHtml = '<div class="vs-stock" style="color:#aa3300">Out of stock</div>';
    } else if (r.restricted) {
      stockHtml = '';
    } else {
      stockHtml = BCS.stockLine(r);
    }

    // Qty cell variants:
    //   - discontinued + no inventory: segmented widget grayed out, both +/- buttons disabled
    //   - restricted:                  segmented widget grayed out, both +/- buttons disabled
    //   - addable:                     interactive segmented widget; clicks call qoAdjustQty
    //                                  (which caps at inventoryQuantity for discontinued items)
    var qtyDisabled = noStockDiscontinued || r.restricted;
    var qtyCell = qoRenderQtyControl(r, qtyDisabled);

    // Price cell variants:
    //   - discontinued + no inventory: hidden (empty)
    //   - pending:                     waiting for customer-context B2B price
    //   - error:                       price unavailable, row stays non-addable
    //   - addable:                     hydrated B2B price
    //   - otherwise:                   em-dash
    var priceCell;
    if (noStockDiscontinued) {
      priceCell = '';
    } else if (!r.restricted && r.priceStatus === 'pending') {
      priceCell = '<span class="qo-price qo-price-dash">Loading…</span>';
    } else if (!r.restricted && r.priceStatus === 'error') {
      priceCell = '<span class="qo-price qo-price-dash">Unavailable</span>';
    } else if (addable) {
      priceCell = '<span class="qo-price">$' + (r.price || 0).toFixed(2) + '</span>';
    } else {
      priceCell = '<span class="qo-price qo-price-dash">—</span>';
    }

    // Vendor line replaces the previous SKU# line. Falls back to '—' if the product has no vendor set.
    var vendorLine = '<div class="qo-resolved-sku">' + (r.vendor ? BCS.escape(r.vendor) : '—') + '</div>';

    // Stock + replacements-link block. The link is rendered inside this wrapper so it sits
    // visually directly under the inventory line (per UX request).
    var stockBlock = '<div class="qo-stock-block">' + stockHtml + replacementsLink + '</div>';

    return ''
      + '<div class="qo-row" data-row-id="' + r.id + '">'
      +   '<div class="qo-resolved-product">'
      +     '<div class="qo-resolved-title">' + BCS.escape(r.title) + badge
      +       ' <button type="button" class="qo-edit" data-action="edit" data-row-id="' + r.id + '" aria-label="Edit row">↩ Edit</button>'
      +     '</div>'
      +     vendorLine
      +     discNote
      +     stockBlock
      +   '</div>'
      +   '<div class="qo-col-qty">' + qtyCell + '</div>'
      +   '<div class="qo-col-price">' + priceCell + '</div>'
      +   '<div class="qo-col-remove">'
      +     '<button type="button" class="qo-remove-btn" data-action="remove" data-row-id="' + r.id + '" aria-label="Remove row">×</button>'
      +   '</div>'
      + '</div>';
  }

  function qoBindRowListeners(r) {
    var input = document.getElementById('qo-sku-' + r.id);
    if (input) {
      input.addEventListener('input', function (e) { qoOnSkuInput(r.id, e.target.value); });
      input.addEventListener('keydown', function (e) { qoOnSkuKeydown(r.id, e); });
      input.addEventListener('focus', function () {
        if (input.value && input.value.length >= QO_MIN_CHARS) qoRunDropdownSearch(r.id, input.value);
      });
      input.addEventListener('blur', function () {
        // Delay close so a click on a dropdown hit registers before the dropdown vanishes.
        setTimeout(function () {
          qoCloseDropdown(r.id);
          qoRestoreEditBackup(r.id);
        }, 200);
      });
    }
    // Bind +/- segmented qty buttons. Skip disabled buttons — the HTML disabled attr blocks
    // clicks anyway, but binding only enabled buttons keeps things clean.
    var rowEl = document.querySelector('.qo-row[data-row-id="' + r.id + '"]');
    if (rowEl) {
      rowEl.querySelectorAll('.qo-qty-btn:not([disabled])').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var delta = parseInt(btn.getAttribute('data-qty-delta'), 10) || 0;
          qoAdjustQty(r.id, delta);
        });
      });
      // Typed qty entry: sanitize live, commit (clamp to >=1) on blur/Enter. On focus we
      // select-all for mouse/keyboard (so typing replaces the value) but NOT for touch — on
      // mobile a tap should place a normal blinking caret, matching the theme cart qty input.
      var qtyInput = rowEl.querySelector('.qo-qty-value');
      if (qtyInput && !qtyInput.disabled) {
        var qtyPointerType = '';
        qtyInput.addEventListener('input', function () { qoOnQtyInput(r.id); });
        qtyInput.addEventListener('blur', function () { qoCommitQtyInput(r.id); });
        qtyInput.addEventListener('pointerdown', function (e) { qtyPointerType = e.pointerType || ''; });
        qtyInput.addEventListener('focus', function () {
          if (qtyPointerType !== 'touch') qtyInput.select();
          qtyPointerType = '';
        });
        qtyInput.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { e.preventDefault(); qtyInput.blur(); }
        });
      }
      rowEl.querySelectorAll('[data-action]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var act = btn.getAttribute('data-action');
          var rid = parseInt(btn.getAttribute('data-row-id'), 10);
          if (act === 'remove') qoRemoveRow(rid);
          else if (act === 'edit') qoEditRow(rid);
          else if (act === 'replacements') qoOpenReplacements(rid);
          else if (act === 'did-you-mean') qoUseDidYouMean(rid);
        });
      });
    }
  }

  // Adjust qty by `delta` (e.g. +1 / -1). Caps at inventoryQuantity for discontinued products,
  // and at a minimum of 1.
  function qoAdjustQty(id, delta) {
    var r = qoFindRow(id);
    if (!r) return;
    qoSetQty(id, (r.qty || 1) + delta);
  }

  // Set qty to an absolute value with validation + cap rules. Updates the display span and
  // the hidden input directly (no full re-render — keeps focus stable).
  function qoSetQty(id, val) {
    var r = qoFindRow(id);
    if (!r) return;
    var n = parseInt(val, 10);
    if (isNaN(n) || n < 1) n = 1;
    var capped = false;
    if (r.discontinued && typeof r.inventoryQuantity === 'number' && r.inventoryQuantity > 0 && n > r.inventoryQuantity) {
      n = r.inventoryQuantity;
      capped = true;
    }
    if (n === r.qty) return;
    r.qty = n;
    var inp = document.getElementById('qo-qty-' + id);
    if (inp) inp.value = String(n);
    if (capped) qoSetStatus('Capped at ' + n + ' — only ' + n + ' available for this discontinued part.', null);
    qoUpdateSubtotal();
    qoUpdateAddToCartButton();
    // qoSetQty intentionally skips qoRenderAll (to keep focus stable on the +/- buttons),
    // so save persistence explicitly here.
    qoSaveState();
  }

  // Live handler while the user types in the qty input. Strips non-digits so letters/symbols
  // never appear, and keeps subtotal / Add-to-Cart state in sync when there's a usable value.
  // We deliberately do NOT clamp or apply the discontinued cap here — the field is allowed to be
  // temporarily empty/partial while editing; the final clamp happens on blur (qoCommitQtyInput).
  function qoOnQtyInput(id) {
    var inp = document.getElementById('qo-qty-' + id);
    if (!inp) return;
    var cleaned = inp.value.replace(/[^0-9]/g, '');
    if (cleaned !== inp.value) inp.value = cleaned;
    var r = qoFindRow(id);
    if (!r) return;
    var n = parseInt(cleaned, 10);
    if (!isNaN(n) && n >= 1) {
      r.qty = n;
      qoUpdateSubtotal();
      qoUpdateAddToCartButton();
      qoSaveState();
    }
  }

  // Commit the typed qty when focus leaves the field (or Enter is pressed). Routes through
  // qoSetQty for the real validation/cap rules, then forces the field to show the canonical
  // value — qoSetQty early-returns when the numeric value is unchanged (e.g. "0"/""→1 while
  // r.qty is already 1, or "007"→7), so we normalize the displayed text here.
  function qoCommitQtyInput(id) {
    var r = qoFindRow(id);
    if (!r) return;
    var inp = document.getElementById('qo-qty-' + id);
    qoSetQty(id, inp ? inp.value : r.qty);
    if (inp) inp.value = String(r.qty);
  }

  // ---------- Autocomplete ----------

  var QO_DEBOUNCE_MS = 220;
  var QO_MIN_CHARS = 2;
  var QO_HITS_PER_PAGE = 8;

  function qoOnSkuInput(id, value) {
    var r = qoFindRow(id);
    if (!r) return;
    r.query = value;
    qoSaveState();
    if (qoSearchTimers[id]) clearTimeout(qoSearchTimers[id]);
    qoSearchTimers[id] = setTimeout(function () { qoRunDropdownSearch(id, value); }, QO_DEBOUNCE_MS);
  }

  function qoOnSkuKeydown(id, e) {
    var dd = document.getElementById('qo-dd-' + id);
    if (!dd || dd.style.display === 'none') return;
    var hits = dd.querySelectorAll('.qo-hit:not(.disabled)');
    if (!hits.length) return;
    var current = dd.querySelector('.qo-hit.highlighted');
    var idx = -1;
    for (var i = 0; i < hits.length; i++) if (hits[i] === current) { idx = i; break; }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      var next = (idx + 1) % hits.length;
      hits.forEach(function (h) { h.classList.remove('highlighted'); h.setAttribute('aria-selected', 'false'); });
      hits[next].classList.add('highlighted');
      hits[next].setAttribute('aria-selected', 'true');
      e.currentTarget.setAttribute('aria-activedescendant', hits[next].id || '');
      hits[next].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      var prev = (idx - 1 + hits.length) % hits.length;
      hits.forEach(function (h) { h.classList.remove('highlighted'); h.setAttribute('aria-selected', 'false'); });
      hits[prev].classList.add('highlighted');
      hits[prev].setAttribute('aria-selected', 'true');
      e.currentTarget.setAttribute('aria-activedescendant', hits[prev].id || '');
      hits[prev].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      var target = current || hits[0];
      var hitIdx = parseInt(target.getAttribute('data-hit-idx'), 10);
      qoPickHit(id, hitIdx);
    } else if (e.key === 'Escape') {
      qoCloseDropdown(id);
    }
  }

  function qoRunDropdownSearch(id, query) {
    var r = qoFindRow(id);
    if (!r) return;
    var dd = document.getElementById('qo-dd-' + id);
    if (!dd) return;

    if (!query || query.length < QO_MIN_CHARS) {
      dd.style.display = 'none';
      dd.innerHTML = '';
      r._dropdownHits = [];
      var input = document.getElementById('qo-sku-' + id);
      if (input) input.removeAttribute('aria-activedescendant');
      return;
    }

    if (!window.algoliaShopify || !window.algoliaShopify.searchClient) {
      dd.innerHTML = '<div class="qo-dropdown-empty">Search unavailable. Please reload the page.</div>';
      dd.style.display = 'block';
      r._dropdownHits = [];
      return;
    }

    qoConfigureLookup();
    BCS.searchSingle(query, QO_HITS_PER_PAGE).then(function (hits) {
      // The user may have typed more characters since this fired; only render if our query
      // is still the row's current query.
      if (r.query !== query) return;
      r._dropdownHits = hits;
      qoActiveDropdownRowId = id;
      if (!hits.length) {
        dd.innerHTML = '<div class="qo-dropdown-empty">No results.</div>';
        dd.style.display = 'block';
        return;
      }
      dd.innerHTML = hits.map(function (h, idx) { return qoRenderHit(id, h, idx); }).join('');
      dd.style.display = 'block';
      qoBindImageFallbacks(dd);
      // Bind click handlers on the rendered hits.
      dd.querySelectorAll('.qo-hit').forEach(function (el) {
        if (el.classList.contains('disabled')) return;
        el.addEventListener('mousedown', function (ev) {
          // mousedown (not click) so the input's blur-to-close handler doesn't race ahead.
          ev.preventDefault();
          var hitIdx = parseInt(el.getAttribute('data-hit-idx'), 10);
          qoPickHit(id, hitIdx);
        });
      });
      // Fan out bom-meta fetches per unique handle to replace retail prices with B2B prices.
      qoOverlayB2BPrices(dd, hits);
      // Fan out bom-alts fetches per unique discontinued+OOS handle to append " - alternative
      // available" to the dropdown row when curated alternatives exist. Cached fetch, fail-soft.
      qoOverlayAltsHint(dd, hits);
    }).catch(function (e) {
      console.error('[BCS Quick Order] Search failed:', e);
      dd.innerHTML = '<div class="qo-dropdown-empty">Search error. Try again.</div>';
      dd.style.display = 'block';
    });
  }

  function qoRenderHit(rowId, h, idx) {
    var rcId = (h.meta && h.meta.custom && h.meta.custom['restricted-class-id']) || '';
    var isRest = BCS.isRestricted(rcId);
    var isDisc = bcsParseBoolMetafield(h.meta && h.meta.custom && h.meta.custom.is_discontinued);
    var img = h.image || h.product_image || '';
    var thumb = img
      ? '<img class="qo-hit-thumb" src="' + BCS.escape(img) + '" alt="" loading="lazy" data-fallback-class="qo-hit-thumb-empty">'
      : '<div class="qo-hit-thumb-empty"></div>';
    var qty = (typeof h.inventory_quantity === 'number') ? h.inventory_quantity : null;
    // Discontinued + no on-hand stock: dropdown should mirror the row-state message
    // ("Product discontinued") instead of the misleading "$price · Out of stock —
    // Available to order". Alternatives data is not in Algolia, so we cannot tell
    // "alternative available" vs "no alternative" here — the row state (bom-meta +
    // bom-alts on pick) resolves that suffix correctly. Price is hidden because the
    // buyer cannot purchase this variant without acting on replacements.
    var isDiscOos = isDisc && !(qty > 0);
    // Restricted products: hide both price and stock — buyer cannot purchase without
    // access, so exposing inventory is misleading. The [Restricted] badge + disabled
    // pick button already communicate the state.
    var stockSpan = isRest ? '' : BCS.stockSpan(qty, h.inventory_management ? true : false);
    // Wrap price in its own span so qoOverlayB2BPrices can target and replace it after
    // bom-meta returns the customer-aware (B2B) price for this variant. Restricted
    // products do not expose prices at all.
    var priceSpan = isRest
      ? ''
      : '<span class="qo-hit-price">Loading…</span>';
    var titleBadges = '';
    if (isDisc) titleBadges += ' <span class="qo-badge discontinued">Discontinued</span>';
    if (isRest) titleBadges += ' <span class="qo-badge restricted">Restricted</span>';
    var pickBtn = isRest
      ? '<button type="button" class="qo-hit-pick" disabled>Restricted</button>'
      : '<button type="button" class="qo-hit-pick">Use this</button>';
    var classes = 'qo-hit' + (isRest ? ' disabled' : '');
    var optionId = 'qo-hit-' + rowId + '-' + idx;
    // Build the meta line robustly so an empty priceSpan (restricted) never leaves a
    // stray leading " · " before the stockSpan, and an empty stockSpan never leaves a
    // trailing " · " after the priceSpan.
    var metaParts = [];
    if (priceSpan) metaParts.push(priceSpan);
    if (stockSpan) metaParts.push(stockSpan);
    var metaHtml = isDiscOos
      ? '<span class="qo-hit-stock discontinued" data-disc-oos="1">Product discontinued</span>'
      : metaParts.join(' · ');
    return ''
      + '<div class="' + classes + '" id="' + optionId + '" role="option" aria-selected="false" data-hit-idx="' + idx + '"'
      +   ' data-handle="' + BCS.escape(h.handle || '') + '"'
      +   ' data-variant-id="' + BCS.escape(String(h.objectID || '')) + '">'
      +   thumb
      +   '<div class="qo-hit-info">'
      +     '<div class="qo-hit-title">' + BCS.escape(h.title || '') + titleBadges + '</div>'
      +     '<div class="qo-hit-meta">' + metaHtml + '</div>'
      +   '</div>'
      +   '<div>' + pickBtn + '</div>'
      + '</div>';
  }

  // After the dropdown renders, fan out bom-meta fetches for each unique handle and replace
  // the retail price text with the customer-aware B2B price as each resolves. Promise cache
  // dedupes concurrent requests, so duplicate handles in the same dropdown share one fetch
  // and repeat searches across rows are free. Fail-soft: if bom-meta fails, the row keeps
  // its retail-price display.
  function qoOverlayB2BPrices(dropdownEl, hits) {
    var seen = {};
    hits.forEach(function (h) {
      var handle = h.handle;
      var rcId = (h.meta && h.meta.custom && h.meta.custom['restricted-class-id']) || '';
      if (BCS.isRestricted(rcId)) {
        var restrictedSel = '.qo-hit[data-handle="' + qoCssEscape(handle) + '"][data-variant-id="' + qoCssEscape(String(h.objectID || '')) + '"] .qo-hit-price';
        dropdownEl.querySelectorAll(restrictedSel).forEach(function (n) { n.textContent = ''; });
        return;
      }
      if (!handle || seen[handle]) return;
      seen[handle] = true;
      BCS.fetchBomMeta(handle).then(function (meta) {
        var handleSelector = qoCssEscape(handle);
        var handleNodes = dropdownEl.querySelectorAll('.qo-hit[data-handle="' + handleSelector + '"] .qo-hit-price');
        if (!meta || !meta.variants) {
          handleNodes.forEach(function (n) { n.textContent = 'Unavailable'; });
          return;
        }
        // The dropdown may have been replaced (user typed more chars); querySelectorAll
        // simply returns no nodes in that case and we silently no-op.
        var loadedVariantIds = {};
        meta.variants.forEach(function (v) {
          var price = (v.price != null) ? parseFloat(v.price) : NaN;
          if (!isFinite(price)) return;
          loadedVariantIds[String(v.id)] = true;
          var sel = '.qo-hit[data-handle="' + handleSelector + '"][data-variant-id="' + qoCssEscape(String(v.id)) + '"] .qo-hit-price';
          var nodes = dropdownEl.querySelectorAll(sel);
          if (!nodes.length) return;
          var newText = '$' + price.toFixed(2);
          nodes.forEach(function (n) { n.textContent = newText; });
        });
        dropdownEl.querySelectorAll('.qo-hit[data-handle="' + handleSelector + '"]').forEach(function (hitEl) {
          var vid = hitEl.getAttribute('data-variant-id');
          if (loadedVariantIds[vid]) return;
          hitEl.querySelectorAll('.qo-hit-price').forEach(function (n) { n.textContent = 'Unavailable'; });
        });
      }).catch(function () {
        dropdownEl.querySelectorAll('.qo-hit[data-handle="' + qoCssEscape(handle) + '"] .qo-hit-price').forEach(function (n) {
          n.textContent = 'Unavailable';
        });
      });
    });
  }

  // After the dropdown renders, fan out bom-alts fetches for each unique discontinued+OOS
  // handle. When curated alternatives exist, append " - alternative available" to the row's
  // "Product discontinued" placeholder so the dropdown matches the row-state message. Fail-soft:
  // if fetch fails or returns no alts, the row keeps its initial text. Promise cache in
  // BCS.fetchBomAlts dedupes concurrent requests, so repeat searches across rows are free.
  // If the dropdown was replaced before the fetch resolved, querySelectorAll returns no nodes
  // and we silently no-op.
  function qoOverlayAltsHint(dropdownEl, hits) {
    var seen = {};
    hits.forEach(function (h) {
      var handle = h.handle;
      if (!handle || seen[handle]) return;
      var qty = (typeof h.inventory_quantity === 'number') ? h.inventory_quantity : null;
      var isDisc = bcsParseBoolMetafield(h.meta && h.meta.custom && h.meta.custom.is_discontinued);
      if (!(isDisc && !(qty > 0))) return;
      seen[handle] = true;
      BCS.fetchBomAlts(handle).then(function (alts) {
        if (!alts) return;
        var hasAlt = (Array.isArray(alts.direct_alternatives) && alts.direct_alternatives.length > 0)
          || (Array.isArray(alts.functional_alternatives) && alts.functional_alternatives.length > 0);
        if (!hasAlt) return;
        var sel = '.qo-hit[data-handle="' + qoCssEscape(handle) + '"] [data-disc-oos="1"]';
        dropdownEl.querySelectorAll(sel).forEach(function (n) {
          n.textContent = 'Product discontinued - alternative available';
        });
      }).catch(function () { /* fail-soft: keep "Product discontinued" */ });
    });
  }

  function qoCloseDropdown(id) {
    var dd = document.getElementById('qo-dd-' + id);
    if (!dd) return;
    dd.style.display = 'none';
    var input = document.getElementById('qo-sku-' + id);
    if (input) input.removeAttribute('aria-activedescendant');
    if (qoActiveDropdownRowId === id) qoActiveDropdownRowId = null;
  }

  // ---------- Picking flow ----------

  function qoPickHit(rowId, hitIdx) {
    var r = qoFindRow(rowId);
    if (!r) return;
    if (r._picking) return;
    var hit = (r._dropdownHits || [])[hitIdx];
    if (!hit) return;
    r._picking = true;

    // Optimistic: switch to resolved state with hit-level info first; bom-meta fills in B2B price + true inventory.
    r.state = 'resolved';
    r.handle = hit.handle || '';
    r.variantId = hit.objectID;
    r.title = hit.title || '';
    r.sku = hit.sku || '';
    r.vendor = hit.vendor || '';
    r.price = 0;
    r.priceStatus = 'pending';
    r.available = (hit.inventory_available !== false);
    r.inventoryQuantity = (typeof hit.inventory_quantity === 'number') ? hit.inventory_quantity : null;
    r.inventoryTracked = false;
    r.restrictedClassId = (hit.meta && hit.meta.custom && hit.meta.custom['restricted-class-id']) || '';
    r.restricted = BCS.isRestricted(r.restrictedClassId);
    r.discontinued = bcsParseBoolMetafield(hit.meta && hit.meta.custom && hit.meta.custom.is_discontinued);
    r.directAlternatives = [];
    r.functionalAlternatives = [];
    r.status = r.restricted ? 'restricted' : (r.discontinued ? 'discontinued' : 'exact');
    qoCloseDropdown(rowId);
    qoRenderAll();

    // Wave 1: bom-meta — overlay B2B price + true inventory + canonical discontinued/restricted flags.
    BCS.fetchBomMeta(r.handle).then(function (meta) {
      var mv = meta && meta.variants ? meta.variants.find(function (v) { return String(v.id) === String(hit.objectID); }) : null;
      if (mv) {
        var hydratedPrice = (mv.price != null) ? parseFloat(mv.price) : NaN;
        if (isFinite(hydratedPrice)) {
          r.price = hydratedPrice;
          r.priceStatus = 'loaded';
        } else {
          r.priceStatus = 'error';
        }
        r.available = (mv.available != null) ? mv.available : r.available;
        r.inventoryQuantity = mv.inventory_quantity;
        r.inventoryTracked = !!mv.inventory_management;
      } else {
        r.priceStatus = 'error';
      }
      // Prefer the canonical vendor from bom-meta if present (template now exposes it).
      if (meta && meta.vendor) r.vendor = meta.vendor;
      r.discontinued = !!(meta && meta.discontinued);
      r.restrictedClassId = (meta && meta.restricted_class_id) || r.restrictedClassId;
      r.restricted = BCS.isRestricted(r.restrictedClassId);
      qoNormalizeRowStatus(r);
      if (!r.discontinued) return null;
      // Wave 2: bom-alts — only for discontinued.
      return BCS.fetchBomAlts(r.handle).then(function (alts) {
        r.directAlternatives = (alts && Array.isArray(alts.direct_alternatives)) ? alts.direct_alternatives : [];
        r.functionalAlternatives = (alts && Array.isArray(alts.functional_alternatives)) ? alts.functional_alternatives : [];
        r.status = (r.directAlternatives.length || r.functionalAlternatives.length) ? 'replacement' : 'discontinued';
      });
    }).catch(function (e) {
      r.priceStatus = 'error';
      console.error('[BCS Quick Order] Pick enrichment failed:', e);
    }).finally(function () {
      r._picking = false;
      r._editBackup = null;
      qoRenderAll();
      // Keep keyboard entry moving through the list after a successful product pick.
      setTimeout(function () {
        var next = qoRows.find(function (row) {
          return row.state !== 'resolved' && row.id !== r.id;
        });
        var nextInput = next ? document.getElementById('qo-sku-' + next.id) : null;
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      }, 0);
    });
  }

  // ---------- Row controls ----------

  function qoRemoveRow(id) {
    qoRows = qoRows.filter(function (r) { return r.id !== id; });
    if (qoRows.length === 0) qoRows.push(qoMakeRow());
    qoRenderAll();
  }

  // True when there's something worth clearing: any resolved row, any typed query, or more
  // rows than the default empty set. Used to enable/disable the "Clear All" button so it can't
  // fire (or prompt) on an already-empty grid.
  function qoHasClearableContent() {
    if (qoRows.length > QO_INITIAL_ROWS) return true;
    return qoRows.some(function (r) {
      return r.state === 'resolved' || !!(r.query && r.query.trim());
    });
  }

  // Removes every line item and resets to a fresh set of empty rows. Mirrors the post–add-to-cart
  // reset: rebuild qoRows, drop stored state, re-render.
  function qoClearAll() {
    if (!qoHasClearableContent()) return;
    if (!window.confirm('Remove all line items? This cannot be undone.')) return;
    qoRows = [];
    for (var i = 0; i < QO_INITIAL_ROWS; i++) qoRows.push(qoMakeRow());
    qoClearStoredState();
    qoRenderAll();
    qoSetStatus('All line items cleared.', 'success');
  }

  // Whether the rendered rows list runs past the bottom of the viewport — i.e. the user would
  // have to scroll the page down to reach the footer's Clear All. Viewport/content-based (no
  // fixed row count), so it adapts to screen size and the number of rows on screen.
  function qoListRequiresScrolling() {
    var rows = document.getElementById('qo-rows');
    if (!rows) return false;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    return rows.getBoundingClientRect().bottom > vh + 8;
  }

  // Show the secondary top "Clear All" only when the list overflows the viewport AND there's
  // something to clear; its enabled state mirrors the bottom button.
  function qoUpdateTopClearAll() {
    var bar = document.getElementById('qo-top-actions');
    if (!bar) return;
    var hasContent = qoHasClearableContent();
    bar.hidden = !(hasContent && qoListRequiresScrolling());
    var btn = document.getElementById('qo-clear-all-top');
    if (btn) btn.disabled = !hasContent;
  }

  // rAF-throttled recompute for scroll/resize, where row state hasn't changed but the
  // viewport relationship has.
  var qoAffordanceRaf = false;
  function qoScheduleTopClearAllUpdate() {
    if (qoAffordanceRaf) return;
    qoAffordanceRaf = true;
    var run = function () { qoAffordanceRaf = false; qoUpdateTopClearAll(); };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    else setTimeout(run, 16);
  }

  function qoEditRow(id) {
    var r = qoFindRow(id);
    if (!r) return;
    var preservedQty = r.qty;
    var preservedQuery = r.query || r.sku;
    var backup = Object.assign({}, r, {
      directAlternatives: Array.isArray(r.directAlternatives) ? r.directAlternatives.slice() : [],
      functionalAlternatives: Array.isArray(r.functionalAlternatives) ? r.functionalAlternatives.slice() : [],
      _dropdownHits: [],
      _editBackup: null,
      _picking: false
    });
    var newRow = qoMakeRow({ id: r.id, qty: preservedQty, query: preservedQuery, state: 'empty', _editBackup: backup });
    var idx = qoRows.indexOf(r);
    if (idx >= 0) qoRows[idx] = newRow;
    qoRenderAll();
    setTimeout(function () {
      var input = document.getElementById('qo-sku-' + id);
      if (input) { input.focus(); input.select(); }
    }, 0);
  }

  function qoRestoreEditBackup(id) {
    var r = qoFindRow(id);
    if (!r || r.state !== 'empty' || !r._editBackup) return;
    var idx = qoRows.indexOf(r);
    if (idx < 0) return;
    qoRows[idx] = r._editBackup;
    qoRenderAll();
  }

  function qoUseDidYouMean(id) {
    var r = qoFindRow(id);
    if (!r || !r.didYouMeanHit) return;
    r._dropdownHits = [r.didYouMeanHit];
    qoPickHit(id, 0);
  }

  // ---------- Replacements modal ----------

  function qoOpenReplacements(rowId) {
    var r = qoFindRow(rowId);
    if (!r) return;
    qoModalReturnFocus = document.activeElement;
    var modal = document.getElementById('qo-modal');
    var titleEl = document.getElementById('qo-modal-title');
    var body = document.getElementById('qo-modal-body');
    titleEl.textContent = 'Replacements for ' + (r.title || r.sku || 'this product');
    body.innerHTML = qoRenderReplacementsBody(r);
    qoBindImageFallbacks(body);
    modal.style.display = 'block';
    setTimeout(function () {
      var closeBtn = modal.querySelector('.qo-modal-close');
      if (closeBtn) closeBtn.focus();
    }, 0);
    body.querySelectorAll('[data-alt-source]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var source = btn.getAttribute('data-alt-source');
        var idx = parseInt(btn.getAttribute('data-alt-idx'), 10);
        qoPickAlternative(rowId, source, idx);
      });
    });
  }

  function qoRenderReplacementsBody(r) {
    var directAlternatives = Array.isArray(r.directAlternatives) ? r.directAlternatives : [];
    var functionalAlternatives = Array.isArray(r.functionalAlternatives) ? r.functionalAlternatives : [];
    var parts = [];
    if (directAlternatives.length) {
      parts.push('<div class="qo-modal-section-title">Direct alternatives</div>');
      parts.push(directAlternatives.map(function (alt, idx) {
        return qoRenderAltCard('direct', alt, idx);
      }).join(''));
    }
    if (functionalAlternatives.length) {
      parts.push('<div class="qo-modal-section-title">Functional alternatives</div>');
      parts.push(functionalAlternatives.map(function (alt, idx) {
        return qoRenderAltCard('functional', alt, idx);
      }).join(''));
    }
    if (!parts.length) parts.push('<p>No alternatives available.</p>');
    return parts.join('');
  }

  function qoRenderAltCard(source, alt, idx) {
    var img = alt.image || '';
    var thumb = img
      ? '<img class="qo-alt-thumb" src="' + BCS.escape(img) + '" alt="" loading="lazy" data-fallback-class="qo-alt-thumb">'
      : '<div class="qo-alt-thumb"></div>';
    var rcId = alt.restricted_class_id || '';
    var isRest = BCS.isRestricted(rcId);
    var qty = (typeof alt.inventory_quantity === 'number') ? alt.inventory_quantity : null;
    var isDisc = bcsParseBoolMetafield(alt.is_discontinued);
    // Discontinued+OOS is non-pickable (no stock to buy); discontinued+in-stock stays pickable
    // so the customer can purchase the last remaining units. Matches the dropdown's qoRenderHit logic.
    var isDiscOos = isDisc && !(qty > 0);
    // Hide price/stock for restricted (no access) or discontinued-with-no-stock (replaced by "Product discontinued" note).
    var pricePart = (isRest || isDiscOos) ? '' : '$' + parseFloat(alt.price || 0).toFixed(2);
    var stockSpan = (isRest || isDiscOos) ? '' : BCS.stockSpan(qty, alt.inventory_management ? true : false);
    var pickBtn = isRest
      ? '<button type="button" class="qo-hit-pick" disabled>Restricted</button>'
      : isDiscOos
        ? '<button type="button" class="qo-hit-pick" disabled>Discontinued</button>'
        : '<button type="button" class="qo-hit-pick" data-alt-source="' + source + '" data-alt-idx="' + idx + '">Use this</button>';
    // Build the meta line robustly so empty parts don't leave stray " · " separators.
    var metaParts = [];
    if (pricePart) metaParts.push(pricePart);
    if (stockSpan) metaParts.push(stockSpan);
    var metaHtml = isDiscOos
      ? '<span class="qo-hit-stock discontinued" data-disc-oos="1">Product discontinued</span>'
      : metaParts.join(' · ');
    var titleBadges = ''
      + (isRest ? ' <span class="qo-badge restricted">Restricted</span>' : '')
      + (isDisc ? ' <span class="qo-badge discontinued">Discontinued</span>' : '');
    return ''
      + '<div class="qo-alt-card">'
      +   thumb
      +   '<div class="qo-hit-info">'
      +     '<div class="qo-hit-title">' + BCS.escape(alt.title || '') + titleBadges + '</div>'
      +     '<div class="qo-hit-meta">' + metaHtml + '</div>'
      +   '</div>'
      +   '<div>' + pickBtn + '</div>'
      + '</div>';
  }

  function qoPickAlternative(rowId, source, idx) {
    var r = qoFindRow(rowId);
    if (!r) return;
    var list = (source === 'direct')
      ? (Array.isArray(r.directAlternatives) ? r.directAlternatives : [])
      : (Array.isArray(r.functionalAlternatives) ? r.functionalAlternatives : []);
    var alt = list[idx];
    if (!alt) return;

    qoCloseModalImpl();

    // Re-run the pick flow with the alternative as if it were a fresh Algolia hit.
    var fakeHit = {
      objectID: alt.variant_id,
      handle: alt.handle,
      title: alt.title,
      sku: alt.sku,
      vendor: alt.vendor || '',
      price: alt.price,
      inventory_quantity: alt.inventory_quantity,
      inventory_available: alt.available,
      meta: { custom: { 'short-desc': alt.short_desc, 'restricted-class-id': alt.restricted_class_id } }
    };
    r._dropdownHits = [fakeHit];
    qoPickHit(rowId, 0);
  }

  function qoCloseModalImpl() {
    var modal = document.getElementById('qo-modal');
    if (modal) modal.style.display = 'none';
    if (qoModalReturnFocus && typeof qoModalReturnFocus.focus === 'function') {
      qoModalReturnFocus.focus();
    }
    qoModalReturnFocus = null;
  }

  function qoHandleModalKeydown(e) {
    var modal = document.getElementById('qo-modal');
    if (!modal || modal.style.display === 'none') return;
    if (e.key === 'Escape') {
      e.preventDefault();
      qoCloseModalImpl();
      return;
    }
    if (e.key !== 'Tab') return;
    var focusable = modal.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // ---------- Add To Cart ----------

  var QO_CART_TIMEOUT_MS = 30000;
  var qoCartInFlight = false;

  function qoMakeSubmissionId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return 'qo:' + window.crypto.randomUUID();
    }
    return 'qo:' + Date.now().toString(36) + ':' + Math.random().toString(36).slice(2, 10);
  }

  function qoConfirmAddedRowIds(submissionId) {
    if (!submissionId) return Promise.resolve([]);
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return [];
        var rowIds = [];
        cart.items.forEach(function (item) {
          var props = item && item.properties ? item.properties : {};
          if (props._qo_submission === submissionId && props._qo_row_id != null) {
            rowIds.push(Number(props._qo_row_id));
          }
        });
        return rowIds;
      })
      .catch(function () { return []; });
  }

  function qoPollAddedRowIds(submissionId, attempts) {
    var tries = attempts || 1;
    return qoConfirmAddedRowIds(submissionId).then(function (rowIds) {
      if (rowIds.length || tries <= 1) return rowIds;
      return new Promise(function (resolve) {
        setTimeout(function () {
          resolve(qoPollAddedRowIds(submissionId, tries - 1));
        }, 500);
      });
    });
  }

  function qoAddToCart() {
    if (qoCartInFlight) return;
    var addable = qoRows.filter(qoIsAddable);
    if (!addable.length) return;

    var submissionId = qoMakeSubmissionId();
    var items = addable.map(function (r) {
      return {
        id: r.variantId,
        quantity: r.qty,
        properties: {
          _qo_submission: submissionId,
          _qo_row_id: String(r.id),
          _qo_source: 'quick_order'
        },
        _rowId: r.id
      };
    });

    var btn = document.getElementById('qo-btn-cart');
    btn.disabled = true;
    var origText = btn.textContent;
    btn.textContent = 'Adding…';
    qoCartInFlight = true;

    var addedRowIds = [];
    var supportsAbortController = typeof AbortController === 'function';
    var controller = supportsAbortController ? new AbortController() : null;
    var timeoutFired = false;
    var timeoutHandle = null;
    if (controller) {
      timeoutHandle = setTimeout(function () {
        timeoutFired = true;
        controller.abort();
      }, QO_CART_TIMEOUT_MS);
    }

    BCS.addItemsToCart(items, {
      openDrawer: true,
      refreshBubble: true,
      signal: controller ? controller.signal : null,
      onProgress: function (succeededBatch, doneCount, totalBatches) {
        succeededBatch.forEach(function (it) { if (it._rowId != null) addedRowIds.push(it._rowId); });
        if (totalBatches > 1) btn.textContent = 'Adding… ' + doneCount + '/' + totalBatches;
      }
    }).then(function () {
      if (timeoutHandle) clearTimeout(timeoutHandle);
      qoCartInFlight = false;
      var n = items.length;
      qoSetStatus('Added ' + n + ' item' + (n !== 1 ? 's' : '') + ' to cart.', 'success');
      // Clears any leftover paste-panel message (e.g. "N SKUs not found.") from an earlier
      // paste action — that message only self-clears on success, so a warning from a prior
      // mixed paste result would otherwise sit there indefinitely after the cart add that
      // followed it, reading as if it still applied to the now-reset table.
      qoSetPasteStatus('', null);
      qoRows = [];
      for (var i = 0; i < QO_INITIAL_ROWS; i++) qoRows.push(qoMakeRow());
      qoClearStoredState();
      qoRenderAll();
      btn.textContent = origText;
    }).catch(function (e) {
      if (timeoutHandle) clearTimeout(timeoutHandle);
      var timedOut = timeoutFired || (e && typeof e.message === 'string' && /timed out|aborted/i.test(e.message));
      var confirmAttempts = timedOut ? 6 : 1;
      qoPollAddedRowIds(submissionId, confirmAttempts).then(function (cartRowIds) {
        cartRowIds.forEach(function (id) {
          if (addedRowIds.indexOf(id) === -1) addedRowIds.push(id);
        });
        qoCartInFlight = false;
        if (addedRowIds.length > 0) {
          qoRows = qoRows.filter(function (r) { return addedRowIds.indexOf(r.id) === -1; });
          qoEnsureEntryRows();
          qoRenderAll();
        }
        btn.disabled = false;
        btn.textContent = origText;
        var msg = (e && e.message) ? e.message : String(e);
        var prefix = addedRowIds.length > 0
          ? addedRowIds.length + ' item' + (addedRowIds.length !== 1 ? 's' : '') + ' already added. '
          : '';
        var timeoutMsg = timedOut ? 'Please check your cart before trying again. ' : '';
        qoSetStatus(prefix + timeoutMsg + 'Cart error: ' + msg, 'error');
        console.error('[BCS Quick Order] Add to cart failed:', e);
        qoUpdateAddToCartButton();
      });
    });
  }

  // ---------- SKU paste ----------
  // Side panel that takes a textarea of SKUs (one per line), runs them through the same
  // batch lookup pipeline as the importer, and merges the resolved rows into qoRows.

  var QO_PASTE_MAX = 100;
  var qoPasteInFlight = false;

  // Parse the textarea content into a deduped list of { sku, qty } entries.
  //
  // Rules:
  //   - Split on \n (newlines).
  //   - Per line: take everything up to the first comma OR tab as the SKU; trim. This
  //     handles three common paste formats:
  //       1. plain SKUs, one per line                     (e.g. "ZN341A")
  //       2. CSV with extra columns                       (e.g. "ZN341A,2,description")
  //       3. Excel multi-column paste (tab-separated)     (e.g. "ZN341A\tDescription\t2")
  //     The space character is NOT a separator, so multi-word SKUs like "RIBU1C PALLET"
  //     are preserved intact (Excel paste from a single cell uses a literal space, not
  //     a tab — tabs only appear between Excel columns).
  //   - Skip blank lines.
  //   - Reject SKUs > 80 chars (defensive — Shopify max is 100; longer paste lines are
  //     likely garbage).
  //   - Dedup case-insensitively via BCS.normalizeSku, keeping the first occurrence's casing.
  //   - **Repeat lines count toward qty**: each occurrence of the same normalized SKU adds 1
  //     to that row's qty. e.g. "B261\nB261\nB261" → one entry { sku: 'B261', qty: 3 }.
  //     Order preserved by first occurrence.
  function qoParsePasteText(text) {
    if (!text) return [];
    var lines = String(text).split('\n');
    var counts = {};      // norm → { sku: original-cased, qty: count }
    var order = [];       // norm keys in first-occurrence order
    for (var i = 0; i < lines.length; i++) {
      var raw = String(lines[i]);
      // Find the first comma or tab on the line.
      var sepIdx = -1;
      for (var c = 0; c < raw.length; c++) {
        var ch = raw.charCodeAt(c);
        if (ch === 9 /* \t */ || ch === 44 /* , */) { sepIdx = c; break; }
      }
      var sku = (sepIdx >= 0 ? raw.slice(0, sepIdx) : raw).trim();
      if (!sku) continue;
      if (sku.length > 80) continue;   // defensive — likely garbage
      var norm = BCS.normalizeSku(sku);
      if (!norm) continue;             // pure punctuation / nothing to match against
      if (counts[norm]) {
        counts[norm].qty += 1;
      } else {
        counts[norm] = { sku: sku, qty: 1 };
        order.push(norm);
      }
    }
    return order.map(function (k) { return counts[k]; });
  }

  function qoSetPasteStatus(text, kind) {
    var el = document.getElementById('qo-paste-status');
    if (!el) return;
    el.textContent = text || '';
    el.className = 'qo-paste-status' + (kind ? ' ' + kind : '');
    if (text && kind === 'success') {
      setTimeout(function () {
        if (el.textContent === text) qoSetPasteStatus('', null);
      }, 4000);
    }
  }

  function qoSetPasteApplyButton(text, disabled) {
    var btn = document.getElementById('qo-paste-apply');
    if (!btn) return;
    btn.textContent = text;
    btn.disabled = !!disabled;
  }

  // Smart-merge new rows into qoRows. Strips truly-blank empty rows from the existing
  // list, appends the new rows, then adds one trailing empty row for further entry.
  function qoApplyPasteMerge(newRows) {
    var kept = qoRows.filter(function (r) {
      if (r.state === 'resolved') return true;
      if (r.unmatched) return true;
      if (r.query && String(r.query).trim() !== '') return true;
      return false;
    });
    function resolvedKey(r) {
      return r && r.state === 'resolved' && r.variantId ? String(r.variantId) : '';
    }
    function unmatchedKey(r) {
      return r && r.state !== 'resolved' && r.query ? BCS.normalizeSku(r.query) : '';
    }
    function addQty(target, amount) {
      var nextQty = (target.qty || 1) + (amount || 1);
      if (target.discontinued && typeof target.inventoryQuantity === 'number' && target.inventoryQuantity > 0 && nextQty > target.inventoryQuantity) {
        nextQty = target.inventoryQuantity;
      }
      target.qty = Math.max(1, nextQty);
    }

    var resolvedIndex = {};
    var unmatchedIndex = {};
    kept.forEach(function (r) {
      var rk = resolvedKey(r);
      var uk = unmatchedKey(r);
      if (rk) resolvedIndex[rk] = r;
      if (!rk && uk) unmatchedIndex[uk] = r;
    });

    newRows.forEach(function (r) {
      var rk = resolvedKey(r);
      var uk = unmatchedKey(r);
      if (rk && resolvedIndex[rk]) {
        addQty(resolvedIndex[rk], r.qty);
        return;
      }
      if (rk && uk && unmatchedIndex[uk]) {
        addQty(r, unmatchedIndex[uk].qty);
        kept = kept.filter(function (existing) { return existing !== unmatchedIndex[uk]; });
        delete unmatchedIndex[uk];
      }
      if (!rk && uk && unmatchedIndex[uk]) {
        addQty(unmatchedIndex[uk], r.qty);
        if (!unmatchedIndex[uk].didYouMeanLabel && r.didYouMeanLabel) {
          unmatchedIndex[uk].didYouMeanLabel = r.didYouMeanLabel;
          unmatchedIndex[uk].didYouMeanHit = r.didYouMeanHit || null;
        }
        return;
      }
      kept.push(r);
      if (rk) resolvedIndex[rk] = r;
      if (!rk && uk) unmatchedIndex[uk] = r;
    });

    qoRows = kept.concat([qoMakeRow()]);
  }

  function qoWaitForSearchClient(timeoutMs) {
    var started = Date.now();
    return new Promise(function (resolve) {
      function check() {
        if (!BCS && window.BCSProductLookup) {
          BCS = window.BCSProductLookup;
          qoConfigureLookup();
        }
        if (BCS && window.algoliaShopify && window.algoliaShopify.searchClient) {
          qoConfigureLookup();
          resolve(true);
          return;
        }
        if (Date.now() - started >= timeoutMs) {
          resolve(false);
          return;
        }
        setTimeout(check, 150);
      }
      check();
    });
  }

  function qoApplyPaste() {
    if (qoPasteInFlight) return;
    var ta = document.getElementById('qo-paste-input');
    if (!ta) return;
    var parsed = qoParsePasteText(ta.value);  // [{ sku, qty }]

    if (!parsed.length) {
      qoSetPasteStatus('Enter at least one SKU.', 'error');
      return;
    }
    if (parsed.length > QO_PASTE_MAX) {
      qoSetPasteStatus(
        'Too many SKUs (' + parsed.length + '). Maximum is ' + QO_PASTE_MAX +
        ' — use the importer below for larger lists.',
        'error'
      );
      return;
    }
    qoPasteInFlight = true;
    qoSetPasteApplyButton('Looking up…', true);
    qoSetPasteStatus(
      'Looking up ' + parsed.length + ' SKU' + (parsed.length !== 1 ? 's' : '') + '…',
      null
    );

    // Stage 1+2: batch Algolia search → score + pick best per SKU.
    //
    // Paste-only tiebreaker for ties at the top score: when a SKU like "RIBU1C" matches
    // both "RIBU1C" and "RIBU1C-PALLET" because they share a supplier-part-number, prefer
    //   (1) the hit whose NORMALIZED internal SKU equals the user's normalized input
    //       (catches the case where one variant's SKU is exactly the user's input)
    //   (2) the hit with the shorter normalized SKU (proxy for "less suffix noise" —
    //       RIBU1C < RIBU1C-PALLET)
    // This is intentionally NOT in BCSProductLookup.scoreHit — applying it to the importer's
    // CSV pipeline could cause regressions on already-good matches; paste-only is safer.
    qoWaitForSearchClient(10000).then(function (searchReady) {
      if (!searchReady) {
        qoSetPasteStatus('Search unavailable. Please reload the page.', 'error');
        return null;
      }

      var skuStrings = parsed.map(function (p) { return p.sku; });
      return BCS.batchAlgoliaSearch(skuStrings).then(function (hitsBySku) {
        // Infrastructure failure (Algolia down / index unconfigured) — surface as a retryable
        // error instead of rendering every pasted SKU as "not found".
        if (hitsBySku && hitsBySku.__lookupFailed) {
          qoSetPasteStatus('Product lookup is temporarily unavailable. Please try again.', 'error');
          return null;
        }
        var picks = parsed.map(function (p) {
          var hits = hitsBySku[p.sku] || [];
          if (!hits.length) return { sku: p.sku, qty: p.qty, hit: null };
          var inNorm = BCS.normalizeSku(p.sku);
          var scored = hits.map(function (h) {
            var s = BCS.scoreHit(p.sku, h);
            var skuNorm = BCS.normalizeSku(h.sku || '');
            return {
              hit: h,
              score: s.score,
              skuExact: (skuNorm && skuNorm === inNorm) ? 1 : 0,
              skuNormLen: skuNorm.length || Infinity
            };
          }).sort(function (a, b) {
            if (b.score !== a.score) return b.score - a.score;          // higher score first
            if (b.skuExact !== a.skuExact) return b.skuExact - a.skuExact; // exact SKU match wins
            return a.skuNormLen - b.skuNormLen;                          // then shortest SKU
          });
          var topStatus = BCS.scoreToStatus(scored[0].score);
          if (topStatus !== 'exact') {
            // Per spec: only exact SPN/SKU matches (score >= 95) auto-resolve.
            // Partial substring matches (e.g. "RIB" -> RIBU1C, score 82) and weak/fuzzy
            // matches must route to "Did you mean" so the user explicitly confirms —
            // silent auto-match risks shipping the wrong product when a SKU is truncated
            // or a brand name like "HON" picks an arbitrary HON-prefixed product.
            //
            // For truly unmatched (fuzzy) inputs, preserve Algolia's relevance order.
            // For partial/weak, keep our score-based pick so paste tie-breakers
            // (prefer internal-SKU exact / shorter SKU) still apply to the suggestion.
            var suggestedHit = (topStatus === 'unmatched') ? hits[0] : scored[0].hit;
            return {
              sku: p.sku,
              qty: p.qty,
              hit: null,
              didYouMeanHit: suggestedHit,
              didYouMeanLabel: suggestedHit.title || suggestedHit.sku || ''
            };
          }
          return { sku: p.sku, qty: p.qty, hit: scored[0].hit };
        });

        var matchedPicks = picks.filter(function (p) { return p.hit; });
        var unmatchedPicks = picks.filter(function (p) { return !p.hit; });

        // Stage 3 (Wave 1): bom-meta per unique handle, parallel. Failures fall back to
        // Algolia values silently (Promise.allSettled-style).
        var uniqueHandles = {};
        matchedPicks.forEach(function (p) {
          if (p.hit.handle) uniqueHandles[p.hit.handle] = true;
        });
        var metaPromises = Object.keys(uniqueHandles).map(function (handle) {
          return BCS.fetchBomMeta(handle).then(
            function (meta) { return { handle: handle, meta: meta }; },
            function () { return { handle: handle, meta: null }; }
          );
        });

        return Promise.allSettled(metaPromises).then(function (settledMetaResults) {
          var metaByHandle = {};
          settledMetaResults.forEach(function (result) {
            if (result.status !== 'fulfilled' || !result.value) return;
            metaByHandle[result.value.handle] = result.value.meta;
          });

          var newRows = [];

          matchedPicks.forEach(function (p) {
            var hit = p.hit;
            var meta = metaByHandle[hit.handle] || null;
            var mv = (meta && meta.variants)
              ? meta.variants.find(function (v) { return String(v.id) === String(hit.objectID); })
              : null;
            var hydratedPrice = (mv && mv.price != null) ? parseFloat(mv.price) : NaN;
            var priceLoaded = isFinite(hydratedPrice);
            var price = priceLoaded ? hydratedPrice : 0;
            var inv = mv ? mv.inventory_quantity
              : ((typeof hit.inventory_quantity === 'number') ? hit.inventory_quantity : null);
            var invTracked = mv ? !!mv.inventory_management : false;
            var available = (mv && mv.available != null) ? mv.available : (hit.inventory_available !== false);
            var rcId = (meta && meta.restricted_class_id)
              || (hit.meta && hit.meta.custom && hit.meta.custom['restricted-class-id'])
              || '';
            var restricted = BCS.isRestricted(rcId);
            var discontinued = !!(meta && meta.discontinued);
            var vendor = (meta && meta.vendor) || hit.vendor || '';
            var status = restricted ? 'restricted' : (discontinued ? 'discontinued' : 'exact');

            // qty comes from the paste — N occurrences of the same SKU sum to qty=N.
            // For discontinued items, cap qty at on-hand inventory (cannot order more than
            // available); if no inventory at all, reset qty to 1 to avoid showing stale numbers
            // in the disabled qty selector.
            var qty = p.qty;
            if (discontinued) {
              if (typeof inv !== 'number' || inv <= 0) {
                qty = 1;
              } else if (qty > inv) {
                qty = inv;
              }
            }

            newRows.push(qoMakeRow({
              state: 'resolved',
              query: p.sku,
              qty: qty,
              handle: hit.handle,
              variantId: hit.objectID,
              title: hit.title || '',
              sku: hit.sku || '',
              vendor: vendor,
              price: price,
              priceStatus: priceLoaded ? 'loaded' : 'error',
              available: available,
              inventoryQuantity: inv,
              inventoryTracked: invTracked,
              restrictedClassId: rcId,
              restricted: restricted,
              discontinued: discontinued,
              status: status,
              directAlternatives: [],
              functionalAlternatives: []
            }));
          });

          unmatchedPicks.forEach(function (p) {
            // Preserve the paste count on unmatched rows too — when the user fixes the SKU
            // (types or picks from autocomplete), the qty stays.
            newRows.push(qoMakeRow({
              state: 'empty',
              query: p.sku,
              qty: p.qty,
              unmatched: true,
              didYouMeanLabel: p.didYouMeanLabel || '',
              didYouMeanHit: p.didYouMeanHit || null
            }));
          });

          // Stage 4: smart-merge into qoRows and render.
          qoApplyPasteMerge(newRows);
          qoRenderAll();

          // Wave 2: bom-alts for discontinued rows, in background. Render again when alts arrive.
          var discontinuedHandles = {};
          newRows.forEach(function (r) {
            if (r.discontinued && !r.restricted && r.handle) discontinuedHandles[r.handle] = true;
          });
          var altPromises = Object.keys(discontinuedHandles).map(function (handle) {
            return BCS.fetchBomAlts(handle).then(function (alts) {
              qoRows.forEach(function (r) {
                if (r.handle !== handle || r.state !== 'resolved' || !r.discontinued) return;
                r.directAlternatives = (alts && Array.isArray(alts.direct_alternatives)) ? alts.direct_alternatives : [];
                r.functionalAlternatives = (alts && Array.isArray(alts.functional_alternatives)) ? alts.functional_alternatives : [];
                r.status = (r.directAlternatives.length || r.functionalAlternatives.length)
                  ? 'replacement'
                  : 'discontinued';
              });
            }, function () { /* silent */ });
          });
          if (altPromises.length) {
            Promise.all(altPromises).then(function () { qoRenderAll(); });
          }

          // Status message + textarea handling.
          var addedCount = matchedPicks.length;
          var unmatchedCount = unmatchedPicks.length;
          if (unmatchedCount === 0) {
            qoSetPasteStatus(
              'Added ' + addedCount + ' product' + (addedCount !== 1 ? 's' : '') + '.',
              'success'
            );
          } else if (addedCount === 0) {
            qoSetPasteStatus(
              unmatchedCount + ' SKU' + (unmatchedCount !== 1 ? 's' : '') + ' not found.',
              'warning'
            );
          } else {
            qoSetPasteStatus(
              'Added ' + addedCount + ' product' + (addedCount !== 1 ? 's' : '') + '. ' +
              unmatchedCount + ' SKU' + (unmatchedCount !== 1 ? 's' : '') + ' not found.',
              'warning'
            );
          }
          ta.value = '';
        });
      });
    }).catch(function (e) {
      console.error('[BCS Quick Order] Paste apply failed:', e);
      qoSetPasteStatus('Lookup failed. Please try again.', 'error');
    }).then(function () {
      qoPasteInFlight = false;
      qoSetPasteApplyButton('Add to Quick Order', false);
    });
  }
})();
